import React from 'react'
import type { ProjectMixProjectRow } from '../../../types/projectAnalysis'
import type { ProjectSortKey } from '../../../utils/projectOverviewLists'
import { ExportMenuButton } from '../../common/export-menu-button'
import {
  exportOverviewCsv,
  exportOverviewExcel,
  getOverviewExportRows,
} from '../../../utils/projectAnalysisExport'

export interface ProjectOverviewTableProps {
  displayedProjects: ProjectMixProjectRow[]
  rowsForExport: ProjectMixProjectRow[]
  remainingCount: number
  onLoadMore: () => void
  tableSubtitle?: string | null
  showTableSubtitle: boolean
  search: string
  onSearchChange: (v: string) => void
  sortBy: ProjectSortKey
  sortOrder: 'asc' | 'desc'
  onToggleSort: (key: ProjectSortKey) => void
  exportLabelParts: string[]
  onRowNavigate: (projectId: number) => void
}

function SortTh({
  label,
  active,
  order,
  onClick,
}: {
  label: string
  active: boolean
  order: 'asc' | 'desc'
  onClick: () => void
}) {
  return (
    <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 cursor-pointer select-none hover:text-slate-700"
      >
        {label}
        {active && (
          <span className="text-blue-500" aria-hidden>
            {order === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </button>
    </th>
  )
}

export function ProjectOverviewTable({
  displayedProjects,
  rowsForExport,
  remainingCount,
  onLoadMore,
  tableSubtitle,
  showTableSubtitle,
  search,
  onSearchChange,
  sortBy,
  sortOrder,
  onToggleSort,
  exportLabelParts,
  onRowNavigate,
}: ProjectOverviewTableProps) {
  const runExportCsv = () => {
    const rows = getOverviewExportRows(rowsForExport)
    exportOverviewCsv(rows, exportLabelParts)
  }
  const runExportExcel = () => {
    const rows = getOverviewExportRows(rowsForExport)
    exportOverviewExcel(rows, exportLabelParts)
  }

  return (
    <section className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold text-slate-900">
            Project-wise hours & utilization
          </h2>
          {showTableSubtitle && tableSubtitle && (
            <p className="text-xs text-slate-500 mt-0.5">{tableSubtitle}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto justify-end">
          <input
            type="search"
            placeholder="Search name, client, category, or ID…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-52 max-w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <ExportMenuButton
            disabled={rowsForExport.length === 0}
            onCsv={runExportCsv}
            onExcel={runExportExcel}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-2 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500 w-10">
                #
              </th>
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[10rem]">
                Project
              </th>
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Category
              </th>
              <SortTh
                label="Total hrs"
                active={sortBy === 'total_hours'}
                order={sortOrder}
                onClick={() => onToggleSort('total_hours')}
              />
              <SortTh
                label="Billable hrs"
                active={sortBy === 'billable_hours'}
                order={sortOrder}
                onClick={() => onToggleSort('billable_hours')}
              />
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Non-bill.
              </th>
              <SortTh
                label="Util. %"
                active={sortBy === 'utilization_pct'}
                order={sortOrder}
                onClick={() => onToggleSort('utilization_pct')}
              />
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Late %
              </th>
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Leave days
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {displayedProjects.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  className="px-3 py-8 text-center text-xs text-slate-500"
                >
                  No projects to display.
                </td>
              </tr>
            ) : (
              <>
                {displayedProjects.map((p, idx) => {
                  const entries = Number(p.entries_count) || 0
                  const late = Number(p.late_entries) || 0
                  const latePct = entries > 0 ? (late / entries) * 100 : null
                  const leaveDays = p.leave_days ?? 0
                  const cat = (p.project_category || '').trim()
                  const client = (p.client_name || '').trim()
                  const title =
                    p.project_name || `Project ${p.project_id}`
                  return (
                    <tr key={p.project_id} className="hover:bg-slate-50">
                      <td className="px-2 py-2 text-center text-xs text-slate-500">
                        {idx + 1}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <button
                          type="button"
                          onClick={() => onRowNavigate(p.project_id)}
                          className="text-left text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
                        >
                          {title}
                        </button>
                        {client ? (
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {client}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {cat ? (
                          <span className="inline-block bg-blue-50 text-blue-700 ring-1 ring-blue-200 text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide">
                            {cat}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 tabular-nums align-top">
                        {(p.total_hours ?? 0).toLocaleString(undefined, {
                          maximumFractionDigits: 1,
                        })}
                      </td>
                      <td className="px-3 py-2 text-xs text-emerald-700 font-medium tabular-nums align-top">
                        {(p.billable_hours ?? 0).toLocaleString(undefined, {
                          maximumFractionDigits: 1,
                        })}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 tabular-nums align-top">
                        {(p.non_billable_hours ?? 0).toLocaleString(undefined, {
                          maximumFractionDigits: 1,
                        })}
                      </td>
                      <td className="px-3 py-2 text-xs font-semibold text-slate-800 tabular-nums align-top">
                        {(p.utilization_pct ?? 0).toFixed(1)}%
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 align-top">
                        {latePct != null ? `${latePct.toFixed(1)}%` : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 tabular-nums align-top">
                        {leaveDays.toLocaleString(undefined, {
                          maximumFractionDigits: 1,
                        })}
                      </td>
                    </tr>
                  )
                })}
                <tr className="bg-slate-50 font-medium">
                  <td className="px-2 py-2" />
                  <td className="px-3 py-2 text-xs text-slate-800">
                    Total{' '}
                    <span className="text-slate-500 font-normal">
                      (rows 1–{displayedProjects.length})
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-400">—</td>
                  <td className="px-3 py-2 text-xs tabular-nums text-slate-700">
                    {displayedProjects
                      .reduce((s, p) => s + (Number(p.total_hours) || 0), 0)
                      .toLocaleString(undefined, { maximumFractionDigits: 1 })}
                  </td>
                  <td className="px-3 py-2 text-xs tabular-nums text-emerald-700">
                    {displayedProjects
                      .reduce((s, p) => s + (Number(p.billable_hours) || 0), 0)
                      .toLocaleString(undefined, { maximumFractionDigits: 1 })}
                  </td>
                  <td className="px-3 py-2 text-xs tabular-nums text-slate-700">
                    {displayedProjects
                      .reduce(
                        (s, p) => s + (Number(p.non_billable_hours) || 0),
                        0,
                      )
                      .toLocaleString(undefined, { maximumFractionDigits: 1 })}
                  </td>
                  <td className="px-3 py-2 text-xs tabular-nums text-slate-700">
                    {(() => {
                      const tot = displayedProjects.reduce(
                        (s, p) => s + (Number(p.total_hours) || 0),
                        0,
                      )
                      const bill = displayedProjects.reduce(
                        (s, p) => s + (Number(p.billable_hours) || 0),
                        0,
                      )
                      return tot > 0 ? `${((bill / tot) * 100).toFixed(1)}%` : '—'
                    })()}
                  </td>
                  <td className="px-3 py-2 text-xs tabular-nums text-slate-700">
                    {(() => {
                      const te = displayedProjects.reduce(
                        (s, p) => s + (Number(p.entries_count) || 0),
                        0,
                      )
                      const tl = displayedProjects.reduce(
                        (s, p) => s + (Number(p.late_entries) || 0),
                        0,
                      )
                      return te > 0 ? `${((tl / te) * 100).toFixed(1)}%` : '—'
                    })()}
                  </td>
                  <td className="px-3 py-2 text-xs tabular-nums text-slate-700">
                    {displayedProjects
                      .reduce((s, p) => s + (Number(p.leave_days) || 0), 0)
                      .toLocaleString(undefined, { maximumFractionDigits: 1 })}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {remainingCount > 0 && (
        <div className="px-4 py-2 border-t border-slate-200">
          <button
            type="button"
            onClick={onLoadMore}
            className="w-full py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
          >
            Load more ({remainingCount} remaining)
          </button>
        </div>
      )}
    </section>
  )
}
